import { NumberInput, TextInput } from '@/components/Field';
import { specOf } from '@/domain/catalog';
import type { FieldSpec } from '@/domain/catalog';
import { inputValueOf, parseInputValue } from '@/lib/format';
import type { ComponentInstance, FieldValue } from '@/domain/types';

function fieldError(field: FieldSpec, raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return '无法识别为数字，保存后不会计入计算';
  if (field.min !== undefined && parsed < field.min) return `不能小于 ${field.min}`;
  if (field.max !== undefined && parsed > field.max) return `不能大于 ${field.max}`;
  return undefined;
}

export interface ComponentFieldsProps {
  component: ComponentInstance;
  onChange: (next: ComponentInstance) => void;
}

/**
 * 一个部件的全部输入项。
 *
 * 受控组件：填入即回调，因此向导里可以边填边看结果。
 * 留空的字段会从 `fields` 中移除，保持「空值即未填」的统一语义。
 */
export function ComponentFields({ component, onChange }: ComponentFieldsProps) {
  const spec = specOf(component.kind);
  /** 同一页里可能有多个同类部件（例如 4 个电机），无障碍名称带上部件名才好区分 */
  const name = component.label?.trim() || spec.label;

  const setField = (key: string, raw: string) => {
    const fields: Record<string, FieldValue> = { ...component.fields };
    const value = parseInputValue(raw);
    if (value === undefined) delete fields[key];
    else fields[key] = value;
    onChange({ ...component, fields });
  };

  const setLabel = (raw: string) => {
    const next = { ...component };
    if (raw.trim()) next.label = raw.trim();
    else delete next.label;
    onChange(next);
  };

  const setQty = (raw: string) => {
    const parsed = Number(raw.trim());
    const qty = Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : spec.defaultQty;
    onChange({ ...component, qty });
  };

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
        {spec.labelEditable ? (
          <TextInput
            label="名称"
            aria-label={`${name} 名称`}
            placeholder={spec.label}
            value={inputValueOf(component.label)}
            onChange={(event) => setLabel(event.target.value)}
            hint="留空则显示部件类型"
          />
        ) : null}
        {spec.quantityEditable ? (
          <NumberInput
            label="数量"
            aria-label={`${name} 数量`}
            unit="个"
            value={String(component.qty)}
            onChange={(event) => setQty(event.target.value)}
            hint={spec.defaultQty === 4 ? '标准四轴为 4 个' : undefined}
          />
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
        {spec.fields.map((field) => {
          const raw = inputValueOf(component.fields[field.key]);
          return (
            <NumberInput
              key={field.key}
              label={field.label}
              aria-label={`${name} ${field.label}`}
              unit={field.unit}
              hint={field.hint}
              placeholder={field.placeholder}
              error={fieldError(field, raw)}
              value={raw}
              onChange={(event) => setField(field.key, event.target.value)}
            />
          );
        })}
      </div>
    </div>
  );
}
